import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Copy, Film, Gauge, Magnet, Mic, Music, Type, Maximize2, Plus, Scissors, Trash2, Volume1, Volume2, VolumeX, X, ZoomIn, ZoomOut } from 'lucide-react';
import {
  clipGap,
  closeAllClipGaps,
  closeClipGap,
  holdClipPositions,
  layOutClips,
  placeClip,
  moveClip,
  roundTime,
  setClipTransition,
  setClipTrim,
  updateAudio,
  updateText,
  clampAudioFades,
  clipSourceEnd,
  normaliseSpeedZones,
  speedOutputSeconds,
  speedTrimForOutput,
  type CutStyle,
  type EditorClip,
  type EditorState,
} from '@rehub/propertycut-timeline';
import type { PropertyCutAsset } from '../../types';
import { CLIP_TRANSITIONS, shownTransition, type EditorSelection } from './model';
import SpeedLane from './SpeedLane';

const TIMELINE_HEIGHT_KEY = 'propertycut.timelineHeight';
const TIMELINE_ROWS_KEY = 'propertycut.timelineRows';
const TIMELINE_SNAP_KEY = 'propertycut.timelineSnap';
/** How close, in pixels, a dragged clip has to get to a neighbour to join it again. */
const SNAP_PX = 10;
type RowId = 'visual' | 'speed' | 'text' | 'music' | 'voice';
const DEFAULT_ROW_HEIGHTS: Record<RowId, number> = { visual: 96, speed: 112, text: 48, music: 56, voice: 56 };
const MIN_ROW_HEIGHT = 28;
const MAX_ROW_HEIGHT = 240;

function clampRowHeight(px: number): number {
  return Math.round(Math.max(MIN_ROW_HEIGHT, Math.min(MAX_ROW_HEIGHT, px)));
}

function loadRowHeights(): Record<RowId, number> {
  if (typeof window === 'undefined') return { ...DEFAULT_ROW_HEIGHTS };
  try {
    const saved = JSON.parse(window.localStorage.getItem(TIMELINE_ROWS_KEY) || '{}') as Partial<Record<RowId, number>>;
    const rows = { ...DEFAULT_ROW_HEIGHTS };
    for (const id of Object.keys(rows) as RowId[]) {
      const value = Number(saved[id]);
      if (Number.isFinite(value) && value > 0) rows[id] = clampRowHeight(value);
    }
    return rows;
  } catch {
    return { ...DEFAULT_ROW_HEIGHTS };
  }
}
const DEFAULT_TIMELINE_HEIGHT = 480;
const MIN_TIMELINE_HEIGHT = 240;
const NARROW_QUERY = '(max-width: 767px)';

function useViewport(): { narrow: boolean; short: boolean } {
  const read = () => ({
    narrow: window.matchMedia(NARROW_QUERY).matches,
    short: window.innerHeight < 520,
  });
  const [viewport, setViewport] = useState(() =>
    typeof window === 'undefined' ? { narrow: false, short: false } : read(),
  );
  useEffect(() => {
    const query = window.matchMedia(NARROW_QUERY);
    const onChange = () => setViewport(read());
    onChange();
    query.addEventListener('change', onChange);
    window.addEventListener('resize', onChange);
    return () => {
      query.removeEventListener('change', onChange);
      window.removeEventListener('resize', onChange);
    };
  }, []);
  return viewport;
}
/** Pixels per second. */
const MIN_ZOOM = 2;
const MAX_ZOOM = 320;
const TRACK_LABEL_PX = 64;
const RULER_STEPS = [1, 2, 5, 10, 15, 30, 60, 120, 300, 600];

function clampZoom(zoom: number): number {
  return Math.round(Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom)) * 100) / 100;
}

/** The slider is logarithmic so zooming far out stays as easy as zooming in. */
function zoomToSlider(zoom: number): number {
  return Math.round((Math.log(clampZoom(zoom) / MIN_ZOOM) / Math.log(MAX_ZOOM / MIN_ZOOM)) * 1000);
}

function sliderToZoom(value: number): number {
  return clampZoom(MIN_ZOOM * (MAX_ZOOM / MIN_ZOOM) ** (value / 1000));
}

function rulerLabel(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function clampTimelineHeight(px: number): number {
  const max = typeof window === 'undefined' ? 900 : Math.max(MIN_TIMELINE_HEIGHT, window.innerHeight * 0.85);
  return Math.round(Math.max(MIN_TIMELINE_HEIGHT, Math.min(max, px)));
}

interface TimelineViewProps {
  state: EditorState;
  assets: PropertyCutAsset[];
  time: number;
  duration: number;
  zoom: number;
  selected: EditorSelection;
  onSelect: (selection: EditorSelection) => void;
  onSeek: (time: number) => void;
  onZoom: (zoom: number) => void;
  onPreview: (state: EditorState) => void;
  onCommit: () => void;
  onSplit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
}

export default function TimelineView(props: TimelineViewProps) {
  const { narrow, short } = useViewport();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const visualRef = useRef<HTMLDivElement>(null);
  const [joinMenu, setJoinMenu] = useState<{ clipId: string; x: number; y: number } | null>(null);
  const [height, setHeight] = useState(() => {
    const saved = Number(typeof window === 'undefined' ? NaN : window.localStorage.getItem(TIMELINE_HEIGHT_KEY));
    return Number.isFinite(saved) && saved > 0 ? clampTimelineHeight(saved) : DEFAULT_TIMELINE_HEIGHT;
  });

  const [rows, setRows] = useState<Record<RowId, number>>(loadRowHeights);
  const [snap, setSnap] = useState(
    () => typeof window === 'undefined' || window.localStorage.getItem(TIMELINE_SNAP_KEY) !== 'off',
  );
  const toggleSnap = () => {
    setSnap((current) => {
      window.localStorage.setItem(TIMELINE_SNAP_KEY, current ? 'off' : 'on');
      return !current;
    });
  };
  const saveRows = (next: Record<RowId, number>) => window.localStorage.setItem(TIMELINE_ROWS_KEY, JSON.stringify(next));
  const resizeRow = (id: RowId, px: number, commit: boolean) => {
    setRows((current) => {
      const next = { ...current, [id]: clampRowHeight(px) };
      if (commit) saveRows(next);
      return next;
    });
  };
  const resetRow = (id: RowId) => {
    setRows((current) => {
      const next = { ...current, [id]: DEFAULT_ROW_HEIGHTS[id] };
      saveRows(next);
      return next;
    });
  };

  const startResize = (event: React.PointerEvent) => {
    event.preventDefault();
    const startY = event.clientY;
    const startHeight = height;
    let latest = height;
    const move = (ev: PointerEvent) => {
      latest = clampTimelineHeight(startHeight + (startY - ev.clientY));
      setHeight(latest);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      if (!window.matchMedia(NARROW_QUERY).matches) {
        window.localStorage.setItem(TIMELINE_HEIGHT_KEY, String(Math.round(latest)));
      }
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };
  const span = Math.max(props.duration, 8);
  const width = TRACK_LABEL_PX + span * props.zoom + 48;
  const tick = RULER_STEPS.find((step) => step * props.zoom >= 44) ?? RULER_STEPS[RULER_STEPS.length - 1];
  const laid = layOutClips(props.state.clips);
  const hasGaps = laid.clips.some((clip) => clipGap(clip) > 0);
  const hasVideo = laid.clips.some(
    (clip) => !clip.blank && props.assets.find((item) => item.id === clip.assetId)?.kind === 'video',
  );

  useEffect(() => {
    if (!joinMenu) return;
    const close = (event: PointerEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.closest('[data-transition-menu]')) return;
      setJoinMenu(null);
    };
    window.addEventListener('pointerdown', close);
    return () => window.removeEventListener('pointerdown', close);
  }, [joinMenu]);

  const seek = (clientX: number) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const rect = scroller.getBoundingClientRect();
    const x = clientX - rect.left + scroller.scrollLeft - TRACK_LABEL_PX;
    props.onSeek(Math.max(0, Math.min(props.duration, x / props.zoom)));
  };

  const [view, setView] = useState<ScrollView>({ left: 0, visible: 1, total: 1 });
  const zoomAnchor = useRef<{ time: number; offset: number } | null>(null);
  const zoomRef = useRef(props.zoom);
  zoomRef.current = props.zoom;
  const onZoomRef = useRef(props.onZoom);
  onZoomRef.current = props.onZoom;

  const measure = useCallback(() => {
    const el = scrollerRef.current;
    if (!el) return;
    setView((current) =>
      current.left === el.scrollLeft && current.visible === el.clientWidth && current.total === el.scrollWidth
        ? current
        : { left: el.scrollLeft, visible: el.clientWidth, total: el.scrollWidth },
    );
  }, []);

  // Keep the moment under the pointer still while zooming with the wheel.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    const anchor = zoomAnchor.current;
    if (el && anchor) {
      el.scrollLeft = Math.max(0, TRACK_LABEL_PX + anchor.time * props.zoom - anchor.offset);
      zoomAnchor.current = null;
    }
    measure();
  }, [props.zoom, width, measure]);

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    // Wheel scrolls the timeline sideways; Shift+wheel scrolls the rows; Ctrl/⌘+wheel or a pinch zooms.
    const onWheel = (event: WheelEvent) => {
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        const offset = event.clientX - el.getBoundingClientRect().left;
        zoomAnchor.current = { time: (el.scrollLeft + offset - TRACK_LABEL_PX) / zoomRef.current, offset };
        onZoomRef.current(clampZoom(zoomRef.current * Math.exp(-event.deltaY * 0.01)));
        return;
      }
      if (event.shiftKey) {
        event.preventDefault();
        el.scrollTop += event.deltaY || event.deltaX;
        return;
      }
      if (Math.abs(event.deltaX) >= Math.abs(event.deltaY)) return;
      event.preventDefault();
      el.scrollLeft += event.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      observer.disconnect();
      el.removeEventListener('wheel', onWheel);
    };
  }, [measure]);

  // Page along with the playhead so it never runs off screen.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const x = TRACK_LABEL_PX + props.time * zoomRef.current;
    if (x < el.scrollLeft + TRACK_LABEL_PX || x > el.scrollLeft + el.clientWidth - 40) {
      el.scrollLeft = Math.max(0, x - el.clientWidth * 0.1);
    }
  }, [props.time]);

  return (
    <div
      className={`relative shrink-0 border-t border-slate-800 bg-slate-950 flex flex-col ${narrow ? 'h-[clamp(72px,32%,220px)] min-h-0 overflow-hidden' : ''}`}
      style={narrow ? undefined : { height }}
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize timeline"
        title="Drag to resize the timeline"
        onPointerDown={startResize}
        onDoubleClick={() => {
          setHeight(DEFAULT_TIMELINE_HEIGHT);
          window.localStorage.removeItem(TIMELINE_HEIGHT_KEY);
        }}
        className="group absolute -top-1.5 inset-x-0 z-30 h-3 cursor-ns-resize touch-none hidden md:block"
      >
        <span className="absolute left-1/2 top-1/2 h-1 w-12 -translate-x-1/2 -translate-y-1/2 rounded-full bg-slate-600 group-hover:bg-teal-300" />
      </div>
      <div className="h-11 md:h-10 shrink-0 px-2 md:px-3 flex items-center gap-1.5 md:gap-2 border-b border-slate-800/80 overflow-x-auto">
        <button type="button" onClick={props.onSplit} className="timeline-tool shrink-0" title="Split at playhead" aria-label="Split at playhead">
          <Scissors className="w-3.5 h-3.5" /> <span className="hidden md:inline">Split</span>
        </button>
        <button type="button" onClick={props.onDuplicate} className="timeline-tool shrink-0" title="Duplicate" aria-label="Duplicate">
          <Copy className="w-3.5 h-3.5" /> <span className="hidden md:inline">Duplicate</span>
        </button>
        <button type="button" onClick={props.onDelete} className="timeline-tool shrink-0" title="Delete" aria-label="Delete">
          <Trash2 className="w-3.5 h-3.5" /> <span className="hidden md:inline">Delete</span>
        </button>
        <div className="flex-1 min-w-1" />
        {hasGaps && (
          <button
            type="button"
            onClick={() => props.onEdit(closeAllClipGaps)}
            className="timeline-tool shrink-0"
            title="Join every clip back up to the one before it"
            aria-label="Close gaps"
          >
            <X className="w-3.5 h-3.5" /> <span className="hidden md:inline">Close gaps</span>
          </button>
        )}
        <button
          type="button"
          onClick={toggleSnap}
          aria-pressed={snap}
          aria-label={snap ? 'Snap on' : 'Snap off'}
          className={`timeline-tool shrink-0 ${snap ? '' : '!border-amber-400/70 !text-amber-200'}`}
          title={
            snap
              ? 'Snap is on: clips stay joined and dragging one changes the order. Turn off to place clips freely. Hold Alt to do it for one drag.'
              : 'Snap is off: drag clips anywhere and leave gaps between them. Turn on to keep clips joined. Hold Alt to snap for one drag.'
          }
        >
          <Magnet className="w-3.5 h-3.5" /> <span className="hidden sm:inline">{snap ? 'Snap on' : 'Snap off'}</span>
        </button>
        <button
          type="button"
          className="timeline-tool shrink-0"
          title="Zoom to fit the whole video"
          aria-label="Zoom to fit the whole video"
          onClick={() => {
            const visible = (scrollerRef.current?.clientWidth ?? 800) - TRACK_LABEL_PX - 48;
            props.onZoom(clampZoom(visible / Math.max(props.duration, 1)));
          }}
        >
          <Maximize2 className="w-3.5 h-3.5" /> <span className="hidden md:inline">Fit</span>
        </button>
        <label className="flex items-center gap-1.5 md:gap-2 text-[11px] text-slate-400 shrink-0">
          <ZoomOut className="w-3.5 h-3.5" />
          <input
            type="range"
            min={0}
            max={1000}
            value={zoomToSlider(props.zoom)}
            onChange={(e) => props.onZoom(sliderToZoom(Number(e.target.value)))}
            aria-label="Timeline zoom"
            className="w-16 md:w-24"
          />
          <ZoomIn className="w-3.5 h-3.5" />
        </label>
      </div>
      <div
        ref={scrollerRef}
        className="flex-1 min-h-0 overflow-auto relative"
        onScroll={measure}
        onPointerDown={(e) => {
          if (e.target === e.currentTarget) props.onSelect(null);
        }}
      >
        <div className="relative" style={{ width }}>
          <div
            className={`h-7 relative border-b border-slate-800 cursor-col-resize select-none ${narrow && short ? 'hidden' : ''}`}
            onPointerDown={(e) => {
              seek(e.clientX);
              const move = (ev: PointerEvent) => seek(ev.clientX);
              const up = () => {
                window.removeEventListener('pointermove', move);
                window.removeEventListener('pointerup', up);
              };
              window.addEventListener('pointermove', move);
              window.addEventListener('pointerup', up);
            }}
          >
            {Array.from({ length: Math.floor(span / tick) + 1 }, (_, i) => (
              <span
                key={i}
                className="absolute top-0 bottom-0 border-l border-slate-700 pl-1 pt-1 text-[11px] leading-none text-slate-500"
                style={{ left: TRACK_LABEL_PX + i * tick * props.zoom }}
              >
                {rulerLabel(i * tick)}
              </span>
            ))}
          </div>

          <Track label="Visual" height={rows.visual} onResize={(h, commit) => resizeRow('visual', h, commit)} onReset={() => resetRow('visual')}>
            <div ref={visualRef} className="relative h-full">
              {laid.clips.map((clip) => {
                const asset = props.assets.find((item) => item.id === clip.assetId);
                const blank = Boolean(clip.blank);
                const title = blank
                  ? (clip.id === props.state.clips[0]?.id
                    ? 'Opening'
                    : clip.id === props.state.clips[props.state.clips.length - 1]?.id
                      ? 'End'
                      : 'Blank')
                  : (asset?.original_filename || 'Clip');
                return (
                  <ClipBlock
                    key={clip.id}
                    title={title}
                    start={clip.start}
                    duration={clip.duration}
                    zoom={props.zoom}
                    selected={props.selected?.type === 'clip' && props.selected.id === clip.id}
                    color={blank ? (clip.background || '#0f172a') : undefined}
                    thumb={!blank && (asset?.kind === 'photo' || asset?.kind === 'floor_plan') ? asset.signed_url : null}
                    sound={asset?.kind === 'video' && !blank ? (clip.muted ? 0 : clip.volume) : null}
                    speedBands={asset?.kind === 'video' && !blank ? speedBands(clip) : undefined}
                    footage={!blank ? footageEdges(clip, asset) : undefined}
                    onPointerDown={(edge, event) =>
                      startGesture(props, 'clip', clip.id, edge, event, visualRef.current, snap)
                    }
                  />
                );
              })}
              {laid.clips.map((clip, index) => {
                const gap = clipGap(clip);
                if (gap === 0) return null;
                const from = index === 0 ? 0 : laid.clips[index - 1].start + laid.clips[index - 1].duration;
                return (
                  <div
                    key={`gap-${clip.id}`}
                    className="group absolute top-1 bottom-1 rounded-md border border-dashed border-slate-700 bg-[repeating-linear-gradient(135deg,transparent_0,transparent_6px,rgba(148,163,184,0.08)_6px,rgba(148,163,184,0.08)_12px)]"
                    style={{ left: from * props.zoom, width: gap * props.zoom }}
                    title={`${gap.toFixed(1)}s gap, shows as black`}
                  >
                    {gap * props.zoom >= 22 && (
                      <button
                        type="button"
                        aria-label="Close gap"
                        title="Close gap"
                        onPointerDown={(event) => event.stopPropagation()}
                        onClick={(event) => {
                          event.stopPropagation();
                          props.onEdit((state) => closeClipGap(state, clip.id));
                        }}
                        className="absolute left-1/2 top-1/2 grid h-5 w-5 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-slate-500 bg-slate-900 text-slate-300 opacity-70 hover:border-rose-400 hover:text-rose-200 group-hover:opacity-100"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                );
              })}
              {laid.clips.slice(1).filter((clip) => clipGap(clip) === 0).map((clip) => {
                const seam = clip.overlap > 0 ? clip.start + clip.overlap / 2 : clip.start;
                const active = clip.transitionIn !== 'cut';
                const label = CLIP_TRANSITIONS.find((item) => item.value === shownTransition(clip.transitionIn))?.label ?? 'Cut';
                return (
                  <button
                    key={`join-${clip.id}`}
                    type="button"
                    data-transition-menu
                    title={active ? `${label} transition` : 'Add a transition'}
                    aria-label={active ? `Change transition, currently ${label}` : 'Add a transition'}
                    className={`absolute z-20 grid h-6 w-6 place-items-center rounded-full border shadow ${
                      active
                        ? 'border-teal-300 bg-teal-400 text-slate-950'
                        : 'border-slate-400 bg-slate-900 text-white hover:border-teal-300 hover:text-teal-200'
                    }`}
                    style={{ left: seam * props.zoom, top: '50%', transform: 'translate(-50%, -50%)' }}
                    onPointerDown={(event) => event.stopPropagation()}
                    onClick={(event) => {
                      event.stopPropagation();
                      const rect = event.currentTarget.getBoundingClientRect();
                      props.onSelect({ type: 'clip', id: clip.id });
                      setJoinMenu((current) => (
                        current?.clipId === clip.id
                          ? null
                          : { clipId: clip.id, x: rect.left + rect.width / 2, y: rect.top }
                      ));
                    }}
                  >
                    <Plus className="w-3 h-3" />
                  </button>
                );
              })}
            </div>
          </Track>

          {hasVideo && (
            <Track label="Speed" height={rows.speed} onResize={(h, commit) => resizeRow('speed', h, commit)} onReset={() => resetRow('speed')}>
              <SpeedLane
                state={props.state}
                assets={props.assets}
                zoom={props.zoom}
                height={rows.speed}
                selected={props.selected}
                onSelect={props.onSelect}
                onPreview={props.onPreview}
                onCommit={props.onCommit}
                onEdit={props.onEdit}
              />
            </Track>
          )}

          <Track label="Text" height={rows.text} onResize={(h, commit) => resizeRow('text', h, commit)} onReset={() => resetRow('text')}>
            <div className="relative h-full">
              {props.state.texts.map((text) => (
                <ClipBlock
                  key={text.id}
                  title={text.text || 'Text'}
                  start={text.start}
                  duration={text.duration}
                  zoom={props.zoom}
                  selected={props.selected?.type === 'text' && props.selected.id === text.id}
                  onPointerDown={(edge, event) => startGesture(props, 'text', text.id, edge, event, null)}
                />
              ))}
            </div>
          </Track>

          {(['music', 'voiceover'] as const).map((role) => (
            <Track
              key={role}
              label={role === 'music' ? 'Music' : 'Voice'}
              height={rows[role === 'music' ? 'music' : 'voice']}
              onResize={(h, commit) => resizeRow(role === 'music' ? 'music' : 'voice', h, commit)}
              onReset={() => resetRow(role === 'music' ? 'music' : 'voice')}
            >
              <div className="relative h-full">
                {props.state.audios
                  .filter((audio) => (audio.role ?? 'music') === role)
                  .map((audio) => {
                    const asset = props.assets.find((item) => item.id === audio.assetId);
                    const name = asset?.original_filename
                      || (audio.provider ? 'Unavailable track' : null)
                      || (audio.musicId ? 'Music' : role === 'voiceover' ? 'Voiceover' : 'Audio');
                    return (
                      <AudioBlock
                        key={audio.id}
                        title={`${name} · ${Math.round(audio.volume * 100)}%`}
                        url={asset?.signed_url ?? null}
                        start={audio.start}
                        duration={audio.duration}
                        fadeIn={audio.fadeIn}
                        fadeOut={audio.fadeOut}
                        zoom={props.zoom}
                        selected={props.selected?.type === 'audio' && props.selected.id === audio.id}
                        onPointerDown={(edge, event) => startGesture(props, 'audio', audio.id, edge, event, null)}
                      />
                    );
                  })}
              </div>
            </Track>
          ))}

          <div
            className="absolute top-0 bottom-0 w-px bg-teal-300 pointer-events-none"
            style={{ left: TRACK_LABEL_PX + props.time * props.zoom }}
          >
            <svg
              viewBox="0 0 12 10"
              className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-2.5 text-teal-300 drop-shadow"
              aria-hidden="true"
            >
              <path d="M0 0H12L6 10Z" fill="currentColor" />
            </svg>
          </div>
        </div>
      </div>
      <HorizontalScrollbar
        view={view}
        onScrollTo={(left) => {
          if (scrollerRef.current) scrollerRef.current.scrollLeft = left;
        }}
      />
      {joinMenu && (
        <JoinMenu
          clipId={joinMenu.clipId}
          x={joinMenu.x}
          y={joinMenu.y}
          state={props.state}
          onEdit={props.onEdit}
          onClose={() => setJoinMenu(null)}
        />
      )}
      <style>{`
        .timeline-tool {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.25rem 0.5rem;
          border-radius: 0.375rem;
          font-size: 12px;
          color: rgb(203 213 225);
          background: rgb(15 23 42);
          border: 1px solid rgb(51 65 85);
        }
        .timeline-tool:hover { color: white; }
      `}</style>
    </div>
  );
}

interface ScrollView {
  left: number;
  visible: number;
  total: number;
}

/** Always-visible left-to-right scrollbar; drag the thumb or click the track to jump. */
function HorizontalScrollbar({ view, onScrollTo }: { view: ScrollView; onScrollTo: (left: number) => void }) {
  const trackRef = useRef<HTMLDivElement>(null);
  const scrollable = view.total > view.visible + 1;
  const fraction = scrollable ? view.visible / view.total : 1;
  const thumbFraction = Math.max(fraction, 0.04);
  const maxLeft = Math.max(0, view.total - view.visible);
  const position = maxLeft > 0 ? view.left / maxLeft : 0;

  const trackWidth = () => trackRef.current?.getBoundingClientRect().width ?? 1;

  const dragThumb = (event: React.PointerEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startLeft = view.left;
    const free = trackWidth() * (1 - thumbFraction);
    const move = (ev: PointerEvent) => {
      if (free <= 0) return;
      onScrollTo(Math.max(0, Math.min(maxLeft, startLeft + ((ev.clientX - startX) / free) * maxLeft)));
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  return (
    <div className="h-4 shrink-0 border-t border-slate-800 bg-slate-950 px-1 flex items-center">
      <div
        ref={trackRef}
        role="scrollbar"
        aria-orientation="horizontal"
        aria-label="Scroll the timeline"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(position * 100)}
        className={`relative h-2.5 flex-1 rounded-full bg-slate-800/70 ${scrollable ? 'cursor-pointer' : 'opacity-40'}`}
        onPointerDown={(event) => {
          if (!scrollable || event.target !== event.currentTarget) return;
          const rect = event.currentTarget.getBoundingClientRect();
          const at = (event.clientX - rect.left) / rect.width - thumbFraction / 2;
          onScrollTo(Math.max(0, Math.min(maxLeft, (at / Math.max(0.0001, 1 - thumbFraction)) * maxLeft)));
        }}
      >
        <div
          className={`absolute inset-y-0 rounded-full ${scrollable ? 'bg-slate-500 hover:bg-teal-300 cursor-grab active:cursor-grabbing' : 'bg-slate-600'}`}
          style={{ left: `${position * (1 - thumbFraction) * 100}%`, width: `${thumbFraction * 100}%` }}
          onPointerDown={scrollable ? dragThumb : undefined}
        />
      </div>
    </div>
  );
}

function JoinMenu({
  clipId,
  x,
  y,
  state,
  onEdit,
  onClose,
}: {
  clipId: string;
  x: number;
  y: number;
  state: EditorState;
  onEdit: (recipe: (state: EditorState) => EditorState) => void;
  onClose: () => void;
}) {
  const clip = state.clips.find((item) => item.id === clipId);
  if (!clip) return null;
  const current = shownTransition(clip.transitionIn);
  const left = Math.min(window.innerWidth - 96, Math.max(96, x));
  const apply = (style: CutStyle) => {
    onEdit((currentState) => setClipTransition(currentState, clipId, style, clip.transitionDuration || 0.5));
    if (style === 'cut') onClose();
  };
  const otherJoins = Math.max(0, state.clips.length - 1);
  const applyToAll = () => {
    onEdit((currentState) => {
      const source = currentState.clips.find((item) => item.id === clipId);
      if (!source) return currentState;
      const style = shownTransition(source.transitionIn);
      const duration = source.transitionDuration || 0.5;
      let next = currentState;
      for (const item of currentState.clips.slice(1)) {
        const sameStyle = shownTransition(item.transitionIn) === style;
        const sameLength = style === 'cut' || item.transitionDuration === (style === 'cut' ? 0 : duration);
        if (sameStyle && sameLength) continue;
        next = setClipTransition(next, item.id, style, duration);
      }
      return next;
    });
    onClose();
  };
  const anyTransitions = otherJoins > 1 && state.clips.slice(1).some((item) => item.transitionIn !== 'cut');
  const removeAll = () => {
    onEdit((currentState) =>
      currentState.clips
        .slice(1)
        .reduce((next, item) => (item.transitionIn === 'cut' ? next : setClipTransition(next, item.id, 'cut', 0)), currentState),
    );
    onClose();
  };
  return (
    <div
      data-transition-menu
      className="fixed z-50 w-44 rounded-lg border border-slate-700 bg-slate-900 p-1.5 shadow-xl"
      style={{ left, top: y - 8, transform: 'translate(-50%, -100%)' }}
    >
      <p className="px-2 py-1 text-[10px] uppercase tracking-wide text-slate-500">Transition</p>
      <div className="grid grid-cols-2 gap-1">
        {CLIP_TRANSITIONS.map((style) => (
          <button
            key={style.value}
            type="button"
            onClick={() => apply(style.value)}
            title={style.hint}
            className={`rounded-md px-2 py-1.5 text-xs ${
              current === style.value ? 'bg-teal-400 text-slate-950' : 'text-slate-200 hover:bg-slate-800'
            }`}
          >
            {style.label}
          </button>
        ))}
      </div>
      {current !== 'cut' && (
        <label className="mt-2 block px-1 text-[11px] text-slate-400">
          Length {clip.transitionDuration.toFixed(1)}s
          <input
            type="range"
            min={0.1}
            max={2}
            step={0.1}
            value={clip.transitionDuration || 0.5}
            onChange={(event) => onEdit((currentState) => setClipTransition(currentState, clipId, current, Number(event.target.value)))}
            className="mt-1 w-full accent-teal-400"
            aria-label="Transition length"
          />
        </label>
      )}
      {current !== 'cut' && (
        <button
          type="button"
          onClick={() => apply('cut')}
          className="mt-2 w-full rounded-md border border-rose-500/50 px-2 py-1.5 text-xs text-rose-200 hover:border-rose-400 hover:bg-rose-500/10"
        >
          Remove transition
        </button>
      )}
      {anyTransitions && (
        <button
          type="button"
          onClick={removeAll}
          className="mt-1 w-full rounded-md px-2 py-1.5 text-xs text-slate-400 hover:bg-slate-800 hover:text-rose-200"
        >
          Remove all transitions
        </button>
      )}
      {otherJoins > 1 && (
        <button
          type="button"
          onClick={applyToAll}
          className="mt-2 w-full rounded-md border border-slate-600 px-2 py-1.5 text-xs text-slate-100 hover:border-teal-300 hover:text-white"
        >
          Apply to all
        </button>
      )}
    </div>
  );
}

const ROW_STYLES: Record<string, { icon: React.ComponentType<{ className?: string }>; label: string; tint: string; bar: string }> = {
  Visual: { icon: Film, label: 'text-teal-300', tint: 'bg-teal-400/[0.07]', bar: 'bg-teal-400' },
  Speed: { icon: Gauge, label: 'text-amber-300', tint: 'bg-amber-400/[0.07]', bar: 'bg-amber-400' },
  Text: { icon: Type, label: 'text-violet-300', tint: 'bg-violet-400/[0.07]', bar: 'bg-violet-400' },
  Music: { icon: Music, label: 'text-pink-300', tint: 'bg-pink-400/[0.07]', bar: 'bg-pink-400' },
  Voice: { icon: Mic, label: 'text-sky-300', tint: 'bg-sky-400/[0.07]', bar: 'bg-sky-400' },
};

function TrackLabel({ label }: { label: string }) {
  const style = ROW_STYLES[label];
  if (!style) {
    return <div className="w-16 shrink-0 text-[11px] uppercase tracking-wide text-slate-500 px-2 py-2">{label}</div>;
  }
  const Icon = style.icon;
  return (
    <div className={`relative w-16 shrink-0 flex flex-col items-center gap-1 pt-2 ${style.tint}`}>
      <span className={`absolute inset-y-0 left-0 w-0.5 ${style.bar}`} />
      <Icon className={`w-4 h-4 ${style.label}`} />
      <span className={`text-[10px] font-semibold uppercase tracking-wide ${style.label}`}>{label}</span>
    </div>
  );
}

function Track({
  label,
  height,
  onResize,
  onReset,
  children,
}: {
  label: string;
  height: number;
  onResize: (height: number, commit: boolean) => void;
  onReset: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="relative flex border-b border-slate-800/70" style={{ height }}>
      <TrackLabel label={label} />
      <div className="flex-1 relative">{children}</div>
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label={`Resize ${label} row`}
        title={`Drag to resize the ${label.toLowerCase()} row · double-click to reset`}
        onDoubleClick={onReset}
        onPointerDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          const startY = event.clientY;
          let latest = height;
          const move = (ev: PointerEvent) => {
            latest = clampRowHeight(height + ev.clientY - startY);
            onResize(latest, false);
          };
          const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
            onResize(latest, true);
          };
          window.addEventListener('pointermove', move);
          window.addEventListener('pointerup', up);
        }}
        className="group absolute inset-x-0 -bottom-1 z-30 h-2 cursor-ns-resize touch-none"
      >
        <span className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-transparent group-hover:bg-teal-300/70" />
      </div>
    </div>
  );
}

interface SpeedBand {
  /** Seconds from the start of the clip on the timeline. */
  start: number;
  end: number;
  speed: number;
}

interface FootageEdges {
  /** Unused source seconds before the clip's first frame and after its last. */
  before: number;
  after: number;
  total: number;
  end: number;
  start: number;
}

/** How much of a video's footage the clip uses. Undefined when the length is unknown. */
function footageEdges(clip: EditorClip, asset: PropertyCutAsset | undefined): FootageEdges | undefined {
  if (asset?.kind !== 'video' || !asset.duration_seconds) return undefined;
  const end = Math.min(asset.duration_seconds, clipSourceEnd(clip));
  return {
    before: Math.max(0, clip.trimStart),
    after: Math.max(0, asset.duration_seconds - end),
    total: asset.duration_seconds,
    start: clip.trimStart,
    end,
  };
}

const EDGE_SECONDS = 0.05;

function speedBands(clip: EditorClip): SpeedBand[] | undefined {
  if (!clip.speedZones?.length) return undefined;
  const trim = clip.trimStart;
  const end = clipSourceEnd(clip);
  return normaliseSpeedZones(clip.speedZones)
    .filter((zone) => zone.end > trim && zone.start < end)
    .map((zone) => ({
      start: speedOutputSeconds(clip.speedZones, trim, Math.max(zone.start, trim)),
      end: speedOutputSeconds(clip.speedZones, trim, Math.min(zone.end, end)),
      speed: zone.speed,
    }));
}

function ClipBlock({
  title,
  start,
  duration,
  zoom,
  selected,
  thumb,
  color,
  sound,
  speedBands,
  footage,
  onPointerDown,
}: {
  title: string;
  start: number;
  duration: number;
  zoom: number;
  selected: boolean;
  thumb?: string | null;
  color?: string;
  /** Video clip sound level, shown as a badge. Null for anything without sound. */
  sound?: number | null;
  speedBands?: SpeedBand[];
  footage?: FootageEdges;
  onPointerDown: (edge: 'left' | 'right' | 'body', event: React.PointerEvent) => void;
}) {
  const atStart = footage ? footage.before < EDGE_SECONDS : false;
  const atEnd = footage ? footage.after < EDGE_SECONDS : false;
  const hint = footage
    ? `${title} · uses ${footage.start.toFixed(1)}–${footage.end.toFixed(1)}s of ${footage.total.toFixed(1)}s` +
      (atEnd ? ' · ends at the end of the video' : ` · ${footage.after.toFixed(1)}s more footage, drag the right edge to use it`)
    : undefined;
  const SoundIcon = sound == null ? null : sound <= 0.001 ? VolumeX : sound < 0.5 ? Volume1 : Volume2;
  return (
    <div
      className={`absolute top-1 bottom-1 rounded-md overflow-hidden border text-[11px] text-white ${
        selected ? 'border-teal-300 ring-1 ring-teal-300' : 'border-slate-600'
      }`}
      style={{
        left: start * zoom,
        width: Math.max(duration * zoom, 6),
        background: color || (thumb ? `center / cover url("${thumb}")` : '#134e4a'),
      }}
      title={hint}
      onPointerDown={(event) => {
        const edge = (event.target as HTMLElement).dataset.edge;
        onPointerDown(edge === 'left' || edge === 'right' ? edge : 'body', event);
      }}
    >
      <span data-edge="left" className="absolute inset-y-0 left-0 w-3 cursor-ew-resize bg-black/30 hover:bg-teal-300/40" />
      <span className={`absolute inset-0 px-2 py-1 truncate pointer-events-none ${color ? 'bg-black/20' : 'bg-black/35'}`}>{title}</span>
      {footage && (
        <>
          <span
            className={`absolute inset-y-0 left-0 pointer-events-none ${atStart ? 'w-[3px] bg-white' : 'border-l-2 border-dashed border-white/60'}`}
          />
          <span
            className={`absolute inset-y-0 right-0 pointer-events-none ${atEnd ? 'w-[3px] bg-white' : 'border-r-2 border-dashed border-white/60'}`}
          />
          {!atEnd && duration * zoom > 70 && (
            <span className="absolute right-3.5 top-1 rounded bg-black/60 px-1 text-[9px] text-slate-200 pointer-events-none">
              +{footage.after.toFixed(1)}s
            </span>
          )}
        </>
      )}
      {speedBands?.map((band, index) => (
        <span
          key={index}
          className={`absolute bottom-0 h-3.5 flex items-center justify-center overflow-hidden text-[9px] font-medium pointer-events-none ${band.speed < 1 ? 'bg-sky-400/60 text-sky-950' : 'bg-amber-300/70 text-amber-950'}`}
          style={{ left: band.start * zoom, width: Math.max(2, (band.end - band.start) * zoom) }}
          title={`${band.speed}× speed`}
        >
          {(band.end - band.start) * zoom > 22 ? `${band.speed}×` : ''}
        </span>
      ))}
      {SoundIcon && (
        <span
          className={`absolute right-3.5 bottom-1 flex items-center gap-0.5 rounded bg-black/60 px-1 text-[9px] pointer-events-none ${sound! <= 0.001 ? 'text-amber-200' : 'text-white'}`}
          title={sound! <= 0.001 ? 'Muted' : `Sound ${Math.round(sound! * 100)}%`}
        >
          <SoundIcon className="w-2.5 h-2.5" />
          {sound! > 0.001 && sound! < 0.995 ? `${Math.round(sound! * 100)}%` : ''}
        </span>
      )}
      <span data-edge="right" className="absolute inset-y-0 right-0 w-3 cursor-ew-resize bg-black/30 hover:bg-teal-300/40" />
    </div>
  );
}

function AudioBlock(props: {
  title: string;
  url: string | null;
  start: number;
  duration: number;
  fadeIn: number;
  fadeOut: number;
  zoom: number;
  selected: boolean;
  onPointerDown: (edge: Edge, event: React.PointerEvent) => void;
}) {
  const peaks = useWaveform(props.url);
  const width = Math.max(props.duration * props.zoom, 6);
  const fadeInPx = Math.min(width, props.fadeIn * props.zoom);
  const fadeOutPx = Math.min(width, props.fadeOut * props.zoom);
  return (
    <div className="absolute top-1 bottom-1" style={{ left: props.start * props.zoom, width: Math.max(props.duration * props.zoom, 6) }}>
      <ClipBlock
        title={props.title}
        start={0}
        duration={props.duration}
        zoom={props.zoom}
        selected={props.selected}
        onPointerDown={props.onPointerDown}
      />
      {peaks && (
        <div className="absolute inset-0 flex items-center gap-px px-2 pointer-events-none opacity-80">
          {peaks.map((peak, index) => (
            <span key={index} className="flex-1 bg-teal-200/80 rounded-sm" style={{ height: `${Math.max(8, peak * 100)}%` }} />
          ))}
        </div>
      )}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox={`0 0 ${width} 100`} preserveAspectRatio="none">
        {fadeInPx > 0 && <polygon points={`0,0 ${fadeInPx},0 0,100`} fill="rgba(2,6,23,0.55)" />}
        {fadeOutPx > 0 && <polygon points={`${width},0 ${width - fadeOutPx},0 ${width},100`} fill="rgba(2,6,23,0.55)" />}
        {fadeInPx > 0 && <line x1={0} y1={100} x2={fadeInPx} y2={0} stroke="#fde68a" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />}
        {fadeOutPx > 0 && <line x1={width} y1={100} x2={width - fadeOutPx} y2={0} stroke="#fde68a" strokeWidth={1.5} vectorEffect="non-scaling-stroke" />}
      </svg>
      {(['fadeIn', 'fadeOut'] as const).map((edge) => {
        const px = edge === 'fadeIn' ? fadeInPx : fadeOutPx;
        const seconds = edge === 'fadeIn' ? props.fadeIn : props.fadeOut;
        return (
          <span
            key={edge}
            role="slider"
            aria-label={edge === 'fadeIn' ? 'Fade in' : 'Fade out'}
            aria-valuenow={seconds}
            title={`${edge === 'fadeIn' ? 'Fade in' : 'Fade out'} ${seconds > 0 ? `${seconds.toFixed(1)}s` : 'off'} · drag to change`}
            onPointerDown={(event) => props.onPointerDown(edge, event)}
            className="absolute top-1 z-10 w-3.5 h-3.5 -translate-x-1/2 rounded-full border border-slate-900 bg-amber-200 cursor-ew-resize shadow"
            style={{ left: edge === 'fadeIn' ? Math.max(18, px) : Math.min(width - 18, width - px) }}
          />
        );
      })}
    </div>
  );
}

function useWaveform(url: string | null): number[] | null {
  const [peaks, setPeaks] = useState<number[] | null>(null);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(url);
        const buffer = await response.arrayBuffer();
        const context = new AudioContext();
        const audio = await context.decodeAudioData(buffer.slice(0));
        const raw = audio.getChannelData(0);
        const bars = 48;
        const size = Math.max(1, Math.floor(raw.length / bars));
        const next: number[] = [];
        for (let i = 0; i < bars; i++) {
          let max = 0;
          for (let j = 0; j < size; j += 8) max = Math.max(max, Math.abs(raw[i * size + j] || 0));
          next.push(max);
        }
        if (!cancelled) setPeaks(next);
        void context.close();
      } catch {
        if (!cancelled) setPeaks(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [url]);
  return peaks;
}

type Edge = 'left' | 'right' | 'body' | 'fadeIn' | 'fadeOut';

function startGesture(
  props: TimelineViewProps,
  kind: 'clip' | 'text' | 'audio',
  id: string,
  edge: Edge,
  event: React.PointerEvent,
  visualTrack: HTMLDivElement | null,
  snap = true,
) {
  event.stopPropagation();
  event.preventDefault();
  const origin = props.state;
  const startX = event.clientX;
  const originStart = layOutClips(origin.clips).clips.find((item) => item.id === id)?.start ?? 0;
  let moved = false;
  /** Snap off: the clip moves on its own and the rest keep their place. Alt flips it for one drag. */
  const freeFor = (ev: PointerEvent) => (snap ? ev.altKey : !ev.altKey);
  const hold = (ev: PointerEvent, next: EditorState, start?: number) =>
    freeFor(ev) ? holdClipPositions(origin, next, id, start) : next;
  const reorderOrPlace = (ev: PointerEvent, dx: number) => {
    if (freeFor(ev)) {
      props.onPreview(placeClip(origin, id, originStart + dx, SNAP_PX / props.zoom));
      return;
    }
    const rect = visualTrack?.getBoundingClientRect();
    const scroller = visualTrack?.closest('.overflow-auto') as HTMLElement | null;
    if (!rect) return;
    const x = ev.clientX - rect.left + (scroller?.scrollLeft ?? 0);
    const others = layOutClips(origin.clips).clips.filter((item) => item.id !== id);
    let index = 0;
    for (const item of others) {
      const mid = item.start * props.zoom + (item.duration * props.zoom) / 2;
      if (x > mid) index += 1;
    }
    props.onPreview(moveClip(origin, id, index));
  };

  const move = (ev: PointerEvent) => {
    const dx = (ev.clientX - startX) / props.zoom;
    if (!moved && Math.abs(ev.clientX - startX) < 4) return;
    moved = true;
    if (kind === 'clip') {
      const clip = origin.clips.find((item) => item.id === id);
      if (!clip) return;
      if (edge === 'body') {
        reorderOrPlace(ev, dx);
        return;
      }
      const leftTrimmed = (next: EditorState) => {
        const after = next.clips.find((item) => item.id === id);
        return hold(ev, next, after ? originStart + (clip.duration - after.duration) : undefined);
      };
      if (clip.blank) {
        const nextDuration = edge === 'right' ? clip.duration + dx : clip.duration - dx;
        const next = setClipTrim(origin, id, 0, nextDuration, null, 'blank');
        props.onPreview(edge === 'right' ? hold(ev, next) : leftTrimmed(next));
        return;
      }
      const asset = props.assets.find((item) => item.id === clip.assetId);
      if (!asset) return;
      if (edge === 'right') {
        props.onPreview(hold(ev, setClipTrim(origin, id, clip.trimStart, clip.duration + dx, asset.duration_seconds, asset.kind)));
      } else if (clip.speedZones?.length) {
        const duration = Math.max(0.25, clip.duration - dx);
        const trim = speedTrimForOutput(clip.speedZones, clipSourceEnd(clip), duration);
        props.onPreview(leftTrimmed(setClipTrim(origin, id, trim, duration, asset.duration_seconds, asset.kind)));
      } else {
        props.onPreview(leftTrimmed(setClipTrim(origin, id, clip.trimStart + dx, clip.duration - dx, asset.duration_seconds, asset.kind)));
      }
      return;
    }
    if (kind === 'text') {
      const text = origin.texts.find((item) => item.id === id);
      if (!text) return;
      if (edge === 'right') {
        props.onPreview(updateText(origin, id, { duration: roundTime(Math.max(0.25, text.duration + dx)) }));
      } else if (edge === 'left') {
        const nextStart = Math.max(0, text.start + dx);
        const consumed = nextStart - text.start;
        props.onPreview(updateText(origin, id, {
          start: roundTime(nextStart),
          duration: roundTime(Math.max(0.25, text.duration - consumed)),
        }));
      } else {
        props.onPreview(updateText(origin, id, { start: roundTime(Math.max(0, text.start + dx)) }));
      }
      return;
    }
    const audio = origin.audios.find((item) => item.id === id);
    const asset = props.assets.find((item) => item.id === audio?.assetId);
    if (!audio) return;
    const source = asset?.duration_seconds ?? null;
    if (edge === 'fadeIn') {
      props.onPreview(updateAudio(origin, id, clampAudioFades(audio.fadeIn + dx, Math.min(audio.fadeOut, audio.duration - audio.fadeIn - dx), audio.duration)));
    } else if (edge === 'fadeOut') {
      props.onPreview(updateAudio(origin, id, clampAudioFades(Math.min(audio.fadeIn, audio.duration - audio.fadeOut + dx), audio.fadeOut - dx, audio.duration)));
    } else if (edge === 'right') {
      let duration = Math.max(0.25, audio.duration + dx);
      if (source) duration = Math.min(duration, Math.max(0.25, source - audio.trimStart));
      props.onPreview(updateAudio(origin, id, { duration: roundTime(duration) }));
    } else if (edge === 'left') {
      let trim = Math.max(0, audio.trimStart + dx);
      let duration = Math.max(0.25, audio.duration - dx);
      if (source) trim = Math.min(trim, Math.max(0, source - 0.25));
      if (source) duration = Math.min(duration, Math.max(0.25, source - trim));
      props.onPreview(updateAudio(origin, id, {
        trimStart: roundTime(trim),
        duration: roundTime(duration),
        start: roundTime(Math.max(0, audio.start + dx)),
      }));
    } else {
      props.onPreview(updateAudio(origin, id, { start: roundTime(Math.max(0, audio.start + dx)) }));
    }
  };

  const up = () => {
    window.removeEventListener('pointermove', move);
    window.removeEventListener('pointerup', up);
    if (!moved) props.onSelect({ type: kind, id });
    else props.onCommit();
  };
  window.addEventListener('pointermove', move);
  window.addEventListener('pointerup', up);
}
